import { lessonAtom } from "@core/grades/atoms";
import type { Resource } from "@core/shared/types";
import { useAction } from "@framework/shared/hooks/useAction";
import { useCallback } from "react";
import { compressActivityPayload } from "../../utils/payload-compression";

type ResourceViewResult = {
  counted: boolean;
  viewsCount: number;
  maxViews: number;
  remainingViews: number;
};

type UseTrackResourceViewReturn = {
  trackResourceView: () => Promise<void>;
};

export function useTrackResourceView(
  resource: Resource,
): UseTrackResourceViewReturn {
  const submit = useAction("/activities/resources");

  const trackResourceView = useCallback(async () => {
    try {
      const payload = {
        type: "view",
        resource: resource.id,
        data: {},
      };

      const compressedPayload = compressActivityPayload(payload);
      const response = await submit(compressedPayload);

      const view: ResourceViewResult | undefined = response?.isOK
        ? (response.data?.view ?? response.data?.data?.view)
        : undefined;

      if (view && typeof view.maxViews === "number") {
        // Trust the server numbers, but do not lock the currently playing
        // resource: the student is watching their last allowed view.
        resource.maxViews = view.maxViews;
        resource.viewsCount = view.viewsCount;
        resource.remainingViews =
          typeof view.remainingViews === "number"
            ? view.remainingViews
            : Math.max(0, view.maxViews - view.viewsCount);

        lessonAtom.update({ ...lessonAtom.value });
      } else if (resource.maxViews) {
        const viewsCount = resource.viewsCount || 0;
        resource.viewsCount = viewsCount + 1;
        resource.remainingViews = Math.max(
          0,
          resource.maxViews - resource.viewsCount,
        );
        if (viewsCount >= resource.maxViews) {
          resource.isLocked = true;
          resource.lockReason = "reachedMaxViews";
        }

        // we need to update the lesson to recheck the lesson resources
        lessonAtom.update({ ...lessonAtom.value });
      }
    } catch (error) {
      console.error("Failed to track resource view:", error);
      // Don't throw error to avoid breaking user experience
    }
  }, [resource.id, submit]);

  return {
    trackResourceView,
  };
}
